import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { showMessage } from "store/modules/Message";
import {
  createInsumoComplete,
  modifyInsumoComplete,
  validationSchemaMovement,
} from "../functions";
import { getAllInsumos } from "components-base/insumos/functions";
import StackInsumosFormFields from "./StackInsumosFormFields";

/** Genera un SKU único basado en el nombre del insumo */
const generateSku = (nombre, variants = []) => {
  const existingSkus = new Set(variants.map((v) => (v.sku || "").toUpperCase()));
  const clean = (nombre || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9\s]/g, "")
    .trim();
  const words = clean.split(/\s+/).filter(Boolean);
  // Primeras 3 letras del primer word + inicial de cada word siguiente
  const base = (words[0] || "SKU").slice(0, 3) + words.slice(1).map((w) => w[0] || "").join("");
  let counter = 1;
  let sku;
  do {
    sku = `${base}-${String(counter).padStart(3, "0")}`;
    counter++;
  } while (existingSkus.has(sku) && counter <= 999);
  return sku;
};

const StackInsumosForm = ({ mode, handleClose, variants = [], onDirtyChange }) => {
  const { defaultData } = useSelector(({ stackInsumos }) => stackInsumos);
  const dispatch = useDispatch();
  const [insumos, setInsumos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Stock base: variante seleccionada (POST) o variante del movimiento a editar (PUT)
  const baseStock = mode === "PUT"
    ? parseFloat(defaultData?.variant?.stock ?? 0)
    : parseFloat(selectedVariant?.stock ?? 0);

  useEffect(() => {
    getAllInsumos().then((data) => setInsumos(data || []));
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues:
      mode === "POST"
        ? {
            insumoId: "",
            sku: "",
            variantStock: 0,
            price: 0,
            active: true,
            attributes: [],
            movementType: "ENTRADA",
            quantity: 0,
            unit: "",
            notes: "",
          }
        : {
            id: defaultData?.id,
            variantId: defaultData?.variant?.id,
            insumoId: defaultData?.variant?.insumo?.id || "",
            sku: defaultData?.variant?.sku || "",
            variantStock: defaultData?.variant?.stock ?? 0,
            price: defaultData?.variant?.price ?? 0,
            active: defaultData?.variant?.active ?? true,
            attributes: (defaultData?.variant?.attributes || []).map((a) => ({
              ...(a.id ? { id: a.id } : {}),
              attributeName: a.attributeName,
              attributeValue: a.attributeValue,
            })),
            movementType: defaultData?.movementType || "ENTRADA",
            quantity: 0,
            unit: defaultData?.unit || "",
            notes: defaultData?.notes || "",
          },
    validationSchema: validationSchemaMovement,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const variantPayload = {
          ...(values.variantId ? { id: values.variantId } : {}),
          insumo: { id: values.insumoId },
          sku: values.sku,
          stock: computedStock,
          price: parseFloat(values.price) || 0,
          active: values.active,
          attributes: values.attributes,
        };

        const payload = {
          variant: variantPayload,
          movementType: values.movementType,
          quantity: parseFloat(values.quantity),
          unit: values.unit,
          notes: values.notes || "",
        };

        const ok =
          mode === "POST"
            ? await createInsumoComplete(payload, dispatch)
            : await modifyInsumoComplete({ ...payload, id: values.id }, dispatch);

        if (ok) handleClose();
      } catch (err) {
        dispatch(
          showMessage({
            message: "Error al guardar. Intenta de nuevo.",
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          })
        );
      } finally {
        setSaving(false);
      }
    },
  });

  // Variantes existentes del insumo seleccionado (solo POST)
  const existingVariants = useMemo(() => {
    if (mode !== "POST" || !formik.values.insumoId) return [];
    return variants.filter((v) => v.insumo?.id === Number(formik.values.insumoId));
  }, [variants, mode, formik.values.insumoId]);

  // Cuando el usuario elige una variante existente: pre-rellena los campos
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    if (variant) {
      formik.setFieldValue("variantId", variant.id);
      formik.setFieldValue("sku", variant.sku || "");
      formik.setFieldValue("price", variant.price ?? 0);
      formik.setFieldValue("active", variant.active ?? true);
      formik.setFieldValue(
        "attributes",
        (variant.attributes || []).map((a) => ({
          ...(a.id ? { id: a.id } : {}),
          attributeName: a.attributeName,
          attributeValue: a.attributeValue,
        }))
      );
      if (variant.insumo?.details?.presentacion) {
        formik.setFieldValue("unit", variant.insumo.details.presentacion);
      }
    } else {
      // Nueva variante: limpiar campos de variante
      formik.setFieldValue("variantId", undefined);
      formik.setFieldValue("sku", "");
      formik.setFieldValue("price", 0);
      formik.setFieldValue("active", true);
      formik.setFieldValue("attributes", []);
    }
  };

  // Stock resultante calculado automáticamente (campo bloqueado en el formulario)
  const computedStock = useMemo(() => {
    const qty = parseFloat(formik.values.quantity) || 0;
    switch (formik.values.movementType) {
      case "ENTRADA": return parseFloat((baseStock + qty).toFixed(2));
      case "SALIDA":  return parseFloat(Math.max(0, baseStock - Math.abs(qty)).toFixed(2));
      case "AJUSTE":  return qty;
      default:        return baseStock;
    }
  }, [formik.values.quantity, formik.values.movementType, baseStock]);

  // Notificar al padre cuando hay cambios sin guardar
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(formik.dirty);
  }, [formik.dirty, onDirtyChange]);

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{
        flex: 1,
        overflowY: "auto",
        p: "20px",
        "& .MuiTextField-root": { marginBottom: "16px" },
      }}
    >
      <StackInsumosFormFields
        formik={formik}
        insumos={insumos}
        computedStock={computedStock}
        baseStock={baseStock}
        mode={mode}
        existingVariants={existingVariants}
        selectedVariant={selectedVariant}
        onVariantSelect={handleVariantSelect}
        onInsumoChange={(insumo) => {
            setSelectedVariant(null);
            if (mode === "POST" && insumo?.nombre) {
              formik.setFieldValue("sku", generateSku(insumo.nombre, variants));
            }
          }}
      />

      <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
        <Grid item>
          <Button variant="outlined" color="secondary" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
        </Grid>
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)",
              color: "#fff",
              fontWeight: 600,
              "&:hover": { background: "linear-gradient(135deg, #e11d48 0%, #db2777 100%)" },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            {saving ? "Guardando..." : mode === "POST" ? "Registrar" : "Guardar cambios"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StackInsumosForm;

